import React, {
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Col, FormInstance, Grid, Row, Text } from 'rsuite';
import { FormModelPerson, Person } from 'src/types/person';
import {
  getInitialFormModelValues,
  mapFormModelToPerson,
} from 'src/common/people';
import { useSelector } from 'react-redux';
import { selectConfigExpressRegistrantOptions } from 'src/store/slices/config/configSelectors';
import { ApplicationData, REGISTRANT_OPTIONS } from 'src/types/flow';
import {
  LOCAL_CONTACT,
  PROPERTY_MANAGEMENT_OPTIONS,
} from 'src/components/payment/constants';
import { useUserRole } from 'src/hooks/useUserRole';
import PersonDetails from '../PersonDetails';
import { FormValuesPerson } from '../Owners/utils';
import { LocalContactRadioGroup } from './LocalContactRadioGroup';
import { PropertyManagerRadioGroup } from './PropertyManagerRadioGroup';
import {
  getOwnerRepValidationModel,
  getPropertyManagerValidationModel,
} from './validations';
import { ExpressRegistrationRegistrant } from 'src/types/configuration';

interface ContactPersonData {
  description: string;
  enabled: boolean;
  optionalFields: Record<string, unknown>;
  verbiage?: string;
}

interface ContactFormProps {
  data: ApplicationData;
  localContactData: ContactPersonData;
  isAdminUpdate?: boolean;
  propertyManagerData: ContactPersonData;
}

export interface ContactFormRef {
  getValues: () => ApplicationData;
  validateForm: () => boolean;
}

export const ContactForm = forwardRef<ContactFormRef, ContactFormProps>(
  (
    {
      data,
      localContactData,
      propertyManagerData,
      isAdminUpdate = false,
    }: ContactFormProps,
    ref
  ) => {
    const propertyManagerForm = useRef<FormInstance<FormModelPerson>>(null);
    const ownerRepForm = useRef<FormInstance<FormModelPerson>>(null);
    const [propertyManagerFormValues, setPropertyManagerFormValues] =
      useState<FormModelPerson>(getInitialFormModelValues());
    const [ownerRepFormValues, setOwnerRepFormValues] =
      useState<FormModelPerson>(getInitialFormModelValues());
    const [propertyManagerFormError, setPropertyManagerFormError] = useState(
      {}
    );
    const [ownerRepFormError, setOwnerRepFormError] = useState({});
    const [localContactError, setLocalContactError] = useState<string>();
    const [selectedContact, setSelectedContact] = useState(LOCAL_CONTACT.OWNER);
    const [selectedManager, setSelectedManager] = useState(
      PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED
    );

    const handleChangeManager = (value: PROPERTY_MANAGEMENT_OPTIONS) => {
      setSelectedManager(value);
      setLocalContactError(undefined);
    };

    const handleChangeContact = (value: LOCAL_CONTACT) => {
      setSelectedContact(value);
      setLocalContactError(undefined);
    };

    const { contacts, registrant, property, tenant } = data;

    const selectedRegistrantOptions = useSelector(
      selectConfigExpressRegistrantOptions
    );

    const { isPropertyManager, isOwner, isTenant, isRegistrant } = useUserRole(
      registrant,
      contacts,
      tenant
    );

    const getPropertyManager = useCallback((): Person => {
      if (selectedManager === PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED) {
        if (isOwner) {
          return { ...(registrant?.owner[0] as Person) };
        }
        return { ...(contacts?.propertyManager as Person) };
      } else if (selectedManager === PROPERTY_MANAGEMENT_OPTIONS.TENANT) {
        return { ...(tenant as Person) };
      }
      return { ...propertyManagerFormValues };
    }, [
      selectedManager,
      isOwner,
      contacts,
      propertyManagerFormValues,
      registrant,
      tenant,
    ]);

    useImperativeHandle(ref, () => ({
      validateForm: () => {
        if (
          selectedContact === LOCAL_CONTACT.OTHER &&
          !ownerRepForm.current?.check()
        ) {
          console.error('Local Contact Form is invalid');
          return false;
        }
        if (showPropertyManagerQuestion) {
          // The form should be evaulated just when the form is present,
          // otherwise it will error
          if (isAdminUpdate || !isPropertyManager || isOwner) {
            if (
              selectedManager ===
                PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER &&
              !propertyManagerForm.current?.check()
            ) {
              console.error(
                'Property Manager Form is invalid',
                selectedManager
              );
              return false;
            }
          }
        }
        if (
          selectedManager === PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED &&
          selectedContact === LOCAL_CONTACT.PROPERTY_MANAGER
        ) {
          setLocalContactError('Please Select a Contact or the Property');
          return false;
        }
        return true;
      },
      getValues: (): ApplicationData => {
        const identifiedPropertyManager: Person = getPropertyManager();
        let localContact: FormModelPerson;

        switch (selectedContact) {
          case LOCAL_CONTACT.PROPERTY_MANAGER:
            localContact = { ...identifiedPropertyManager };
            break;
          case LOCAL_CONTACT.TENANT:
            localContact = { ...(tenant as Person) };
            break;
          case LOCAL_CONTACT.OWNER:
          default:
            localContact = { ...(registrant?.owner?.[0] as Person) };
            break;
          case LOCAL_CONTACT.OTHER:
            localContact = { ...ownerRepFormValues };
            break;
        }

        return {
          contacts: {
            selectedManagementOption: selectedManager,
            selectedOwnerRepOption: selectedContact,
            propertyManager: mapFormModelToPerson(identifiedPropertyManager),
            localContact: mapFormModelToPerson(localContact),
          },
        } as ApplicationData;
      },
    }));

    const getSameAsPropertyAddress = useCallback(
      (person: Person) => {
        return Boolean(
          person.mailingAddress &&
            person.mailingAddress.toLowerCase() ===
              property?.propertyaddress.toLowerCase()
        );
      },
      [property]
    );

    useEffect(() => {
      // Initial Values of the Form
      if (contacts !== undefined) {
        // Management Radio Option
        setSelectedManager(
          (prev) => contacts?.selectedManagementOption ?? prev
        );
        // Selected Contact
        setSelectedContact((prev) => contacts?.selectedOwnerRepOption ?? prev);

        // Values of Local Contact if Selection is OTHER
        if (contacts.selectedOwnerRepOption === LOCAL_CONTACT.OTHER) {
          const person = contacts.localContact as Person;
          setOwnerRepFormValues({
            ...person,
            sameAsPropertyAddress: getSameAsPropertyAddress(person),
          });
        }
        // Values of Property Manager if Selection is PROPERTY_MANAGER
        if (
          contacts.selectedManagementOption ===
          PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER
        ) {
          const person = contacts.propertyManager as Person;
          setPropertyManagerFormValues({
            ...person,
            sameAsPropertyAddress: getSameAsPropertyAddress(person),
          });
        }
      }
    }, [contacts, getSameAsPropertyAddress]);

    const showPropertyManagerQuestion = useMemo((): boolean => {
      return (
        isAdminUpdate ||
        (propertyManagerData.enabled && (isPropertyManager || !!isOwner))
      );
    }, [propertyManagerData, isOwner, isPropertyManager, isAdminUpdate]);

    const canChangePropertyManager = useMemo(() => {
      // Can't remove itself as property manager
      return (isOwner && isRegistrant) || isAdminUpdate;
    }, [isRegistrant, isOwner, isAdminUpdate]);

    const canChangePropertyManagerDetails = useMemo(() => {
      return isRegistrant || isAdminUpdate;
    }, [isRegistrant, isAdminUpdate]);

    const propertyManagerValidationModel = getPropertyManagerValidationModel(
      propertyManagerData.optionalFields
    );

    const ownerRepValidationModel = getOwnerRepValidationModel(
      localContactData.optionalFields
    );

    const onOwnerRepSameAsPropertyAddressChanged = (checked: boolean) => {
      setOwnerRepFormValues((prevFormValues: FormModelPerson) => ({
        ...prevFormValues,
        sameAsPropertyAddress: checked,
        mailingAddress: checked
          ? property?.propertyaddress ?? ''
          : prevFormValues.mailingAddress,
      }));
    };

    const onPropertyManagerSameAsPropertyAddressChanged = (
      checked: boolean
    ) => {
      setPropertyManagerFormValues((prevFormValues: FormModelPerson) => ({
        ...prevFormValues,
        sameAsPropertyAddress: checked,
        mailingAddress: checked
          ? property?.propertyaddress ?? ''
          : prevFormValues.mailingAddress,
      }));
    };

    return (
      <Grid
        className="mt-12 ml-0 "
        fluid
      >
        {showPropertyManagerQuestion && (
          <>
            <Row className="mb-16">
              <Col xs={24}>
                <Text className="text-bold mb-8">
                  Property Management Company
                </Text>
                <Text>{propertyManagerData.description}</Text>
              </Col>
            </Row>
            <Row>
              <Col xs={24}>
                <PropertyManagerRadioGroup
                  disabled={!canChangePropertyManager}
                  isOwner={isOwner}
                  isPropertyManager={isPropertyManager}
                  onChange={handleChangeManager}
                  value={selectedManager}
                  isAdminUpdate={isAdminUpdate}
                  configuration={
                    selectedRegistrantOptions as ExpressRegistrationRegistrant
                  }
                />
              </Col>
            </Row>
            {(!isPropertyManager || isOwner) &&
              selectedManager ===
                PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER && (
                <>
                  <Row className="mt-8 mb-8">
                    <Col xs={12}>
                      <Text style={{ fontWeight: 'bold' }}>
                        Property Manager Information
                      </Text>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <PersonDetails
                        id="propertyManager"
                        ref={propertyManagerForm}
                        formModel={FormValuesPerson}
                        formError={propertyManagerFormError}
                        formValues={propertyManagerFormValues}
                        validationModel={propertyManagerValidationModel}
                        setFormError={setPropertyManagerFormError}
                        setFormValues={setPropertyManagerFormValues}
                        showSameAsPropertyAddressCheckboxOption={true}
                        onSameAsPropertyAddressChanged={
                          onPropertyManagerSameAsPropertyAddressChanged
                        }
                        optionalFields={propertyManagerData.optionalFields}
                        disabled={!canChangePropertyManagerDetails}
                      />
                    </Col>
                  </Row>
                </>
              )}
          </>
        )}
        <Row
          className={`mb-16 ${registrant?.registrantType !== REGISTRANT_OPTIONS.PROPERTY_MANAGER && 'mt-24'}`}
        >
          <Col xs={24}>
            <Text className="text-bold mb-8">{localContactData.verbiage}</Text>
            <Text>{localContactData.description}</Text>
          </Col>
        </Row>
        <Row>
          <Col xs={24}>
            <LocalContactRadioGroup
              isOwner={isOwner}
              isTenant={isTenant}
              isPropertyManager={isPropertyManager}
              localContactVerbiage={localContactData.verbiage ?? ''}
              onChange={handleChangeContact}
              propertyManager={selectedManager}
              value={selectedContact}
              error={localContactError}
            />
          </Col>
        </Row>
        {selectedContact === LOCAL_CONTACT.OTHER && (
          <>
            <Row className="mt-24 mb-8">
              <Col xs={12}>
                <Text style={{ fontWeight: 'bold' }}>
                  {localContactData.verbiage} Information
                </Text>
              </Col>
            </Row>
            <Row>
              <Col>
                <PersonDetails
                  id="ownerRepForm"
                  ref={ownerRepForm}
                  formModel={FormValuesPerson}
                  formError={ownerRepFormError}
                  formValues={ownerRepFormValues}
                  validationModel={ownerRepValidationModel}
                  setFormError={setOwnerRepFormError}
                  setFormValues={setOwnerRepFormValues}
                  showSameAsPropertyAddressCheckboxOption={true}
                  onSameAsPropertyAddressChanged={
                    onOwnerRepSameAsPropertyAddressChanged
                  }
                  optionalFields={localContactData.optionalFields}
                />
              </Col>
            </Row>
          </>
        )}
      </Grid>
    );
  }
);
